import { BLOG_PATH, HOME_PATH, PAGE_PATH, POLICY_PATH } from '~/constants';
import type {
  CreateNavigationItemParams,
  CreateNavigationItemsParams,
  CreateNavigationSubmenuParams,
  NavigationItem,
  NavigationItems,
} from '~/types/sanity/navigation';
import { checkAuthVisibility } from '~/utils/auth';
import { isPageUrlType } from '~/utils/link';

/**
 * A type guard to check if an item is a valid navigation item
 * @param item - the navigation item to check from the Sanity schema
 * @returns - whether the item is a valid navigation item
 */
const isNavigationItem = (item: NavigationItems[number] | NavigationItem): item is NavigationItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item._key === 'string' &&
    typeof item.linkType === 'string' &&
    typeof item.label === 'string' &&
    typeof item.authVisibility === 'string' &&
    (typeof item.featureFlag === 'string' || item.featureFlag === null) &&
    ('urlType' in item ? typeof item.urlType === 'string' : true) &&
    ('url' in item ? typeof item.url === 'string' : true) &&
    (!('submenu' in item) || item.submenu === null || Array.isArray(item.submenu))
  );
};

export const useNavigationMenu = () => {
  const createNavigationSubmenu = (params: CreateNavigationSubmenuParams): NavigationItem[] | null => {
    const { item, isAuthenticated, flags } = params;
    if (!Array.isArray(item.submenu)) {
      return null;
    }

    return item.submenu
      .map((submenuItem) => createNavigationItem({ item: submenuItem, isAuthenticated, flags }))
      .filter((submenuNavItem): submenuNavItem is NavigationItem => !!submenuNavItem);
  };

  /**
   * Creates a navigation item in a consistent format from the Sanity query schema
   * @param params - the parameters to create a navigation item in a consistent format
   * @returns - the navigation item in a consistent format
   */
  const createNavigationItem = (params: CreateNavigationItemParams): NavigationItem | null => {
    const { item, isAuthenticated, flags } = params;
    if (!isNavigationItem(item) || !item.urlType || !item.url) {
      return null;
    }

    // Depending on the item's auth visibility, we may need to hide the item
    if (!checkAuthVisibility(item.authVisibility, isAuthenticated)) {
      return null;
    }

    // Depending on the item's feature flag, we may need to hide the item
    if (item.featureFlag && !flags?.[item.featureFlag]) {
      return null;
    }

    let formattedUrl = item.url;
    if (item.url.includes('homepage')) {
      formattedUrl = HOME_PATH;
    } else if (isPageUrlType(item.urlType)) {
      formattedUrl = `${PAGE_PATH}${item.url}`;
    } else if (item.urlType === 'blogArticle') {
      formattedUrl = `${BLOG_PATH}${item.url}`;
    } else if (item.urlType === 'policy') {
      formattedUrl = `${POLICY_PATH}${item.url}`;
    }

    return {
      _key: item._key,
      linkType: item.linkType,
      icon: item.icon,
      label: item.label,
      authVisibility: item.authVisibility,
      featureFlag: item.featureFlag,
      urlType: item.urlType,
      url: formattedUrl,
      submenu: createNavigationSubmenu({ item, isAuthenticated, flags }),
      id: item.id,
    };
  };

  const createNavigationItems = (params: CreateNavigationItemsParams): NavigationItem[] => {
    const { items, isAuthenticated, flags } = params;
    return (
      items
        .map((item) => createNavigationItem({ item, isAuthenticated, flags }))
        .filter((item): item is NavigationItem => !!item) ?? []
    );
  };

  return { createNavigationItem, createNavigationItems };
};
